import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import api from "../../../services/api";
import { useSnackbarAlert } from "../../../hooks/useSnackbarAlert";

const UpdateCreditsModal = ({ member, open, onClose, onSuccess }) => {
  const snackbar = useSnackbarAlert();

  const [creditAdjustment, setCreditAdjustment] = useState(""); // keep as typed string
  const [reason, setReason] = useState("customer complaint");
  const [employeePasscode, setEmployeePasscode] = useState("");
  const [error, setError] = useState("");
  const [confirmLargeCredit, setConfirmLargeCredit] = useState(false);
  const [lastRequestedAmount, setLastRequestedAmount] = useState(null);
  const [pendingSign, setPendingSign] = useState(1);

  const reasons = [
    "customer complaint",
    "redemption",
    "promotional",
    "customer appreciation",
    "employee error",
    "fraudulent activity",
    "account adjustment",
    "program violation",
    "system error",
  ];

  const validatePasscode = async (passcode) => {
    if (!passcode) throw new Error("Employee passcode is required.");
    const response = await api.post("/admin/employees/validatePasscode", {
      passcode,
    });
    if (!response.data.valid) throw new Error("Invalid employee passcode.");
  };

  // Allow negatives and up to 2 decimals while typing.
  // Accept both '.' and ',' (we normalize to '.')
  const handleCreditChange = (val) => {
    const next = val.replace(",", "."); // normalize
    const pattern = /^-?\d*(\.\d{0,2})?$/; // optional leading '-' and up to 2 dp
    if (pattern.test(next)) setCreditAdjustment(next);
  };

  // On blur, format to 2 dp if we have a number
  const handleCreditBlur = () => {
    const v = creditAdjustment;
    if (!v || v === "-" || v === "." || v === "-.") return;
    const n = parseFloat(v);
    if (!Number.isNaN(n)) setCreditAdjustment(n.toFixed(2));
  };

  const clearFields = () => {
    setCreditAdjustment("");
    setReason("customer complaint");
    setEmployeePasscode("");
    setConfirmLargeCredit(false);
    setLastRequestedAmount(null);
    setPendingSign(1);
    setError("");
  };

  const handleUpdateCredits = async () => {
    setError("");

    // validate input
    if (
      !creditAdjustment ||
      creditAdjustment === "-" ||
      creditAdjustment === "." ||
      creditAdjustment === "-."
    ) {
      setError("Please enter a valid credit adjustment value.");
      return;
    }

    const creditAmount = parseFloat(creditAdjustment.replace(",", "."));
    if (Number.isNaN(creditAmount)) {
      setError("Please enter a valid credit adjustment value.");
      return;
    }
    if (!reason) {
      setError("Please select a reason for the credit adjustment.");
      return;
    }

    // confirm when absolute adjustment exceeds threshold (works for negative too)
    if (Math.abs(creditAmount) > 100 && !confirmLargeCredit) {
      setLastRequestedAmount(Math.abs(creditAmount));
      setPendingSign(Math.sign(creditAmount) || 1);
      setConfirmLargeCredit(true);
      return;
    }

    try {
      await validatePasscode(employeePasscode);

      await api.post(`/admin/digitalLoyalty/${member._id}/updateCredit`, {
        credit: creditAmount, // can be negative
        reason,
        employeeId: employeePasscode,
      });

      const verb = creditAmount >= 0 ? "added" : "deducted";
      snackbar.success(
        `${Math.abs(creditAmount).toFixed(2)} credit ${verb} successfully!`
      );
      clearFields();
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err.message || "Failed to update credit.");
    }
  };

  const largeVerb = pendingSign >= 0 ? "add" : "deduct";

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}>
        <Typography
          variant="h6"
          gutterBottom
          align="center"
          sx={{ mb: 3, fontWeight: "bold" }}>
          Update Credits
        </Typography>

        {error && (
          <Typography color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}

        <TextField
          label="Credit Adjustment"
          fullWidth
          margin="normal"
          value={creditAdjustment}
          onChange={(e) => handleCreditChange(e.target.value)}
          onBlur={handleCreditBlur}
          // show numeric keyboard on mobile but still allow '-' and '.'
          inputProps={{ inputMode: "decimal" }}
          placeholder="e.g. 25.00 or -10.50"
        />

        <FormControl fullWidth margin="normal">
          <InputLabel id="reason-label">Reason</InputLabel>
          <Select
            labelId="reason-label"
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}>
            {reasons.map((reasonOption, index) => (
              <MenuItem key={index} value={reasonOption}>
                {reasonOption}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Employee Passcode"
          type="password"
          fullWidth
          margin="normal"
          value={employeePasscode}
          onChange={(e) => setEmployeePasscode(e.target.value)}
        />

        {confirmLargeCredit && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: "#fff3cd",
              border: "1px solid #ffeeba",
              borderRadius: 1,
              color: "#856404",
            }}>
            <Typography fontWeight="bold" sx={{ mb: 1 }}>
              Large Credit Adjustment Warning
            </Typography>
            <Typography sx={{ mb: 2 }}>
              You're about to {largeVerb} a credit of $
              {lastRequestedAmount?.toFixed(2)}. Please confirm this amount is
              accurate.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              color="warning"
              onClick={handleUpdateCredits}>
              Confirm and Update Credit
            </Button>
          </Box>
        )}

        {!confirmLargeCredit && (
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleUpdateCredits}
            sx={{ mt: 2 }}>
            Update Credit
          </Button>
        )}
      </Box>
    </Modal>
  );
};

export default UpdateCreditsModal;
